import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { X } from "lucide-react";

/**
 * Strictly car.gr-compliant equipment selector.
 * - Lists the 85 official car extras from `/api/cargr-lookups/extras` (extras.csv).
 * - Free search filter.
 * - "Orphan" items (legacy equipment NOT in extras.csv) shown in a separate dimmed
 *   section with a remove button so they don't silently break the car.gr feed.
 *
 * value is stored as List[str] using the exact name from extras.csv so the backend
 * mapper resolves to feature IDs without ambiguity.
 */
export default function EquipmentSelector({ value = [], onChange }) {
  const [extras, setExtras] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/cargr-lookups/extras").then((r) => setExtras(r.data.extras || []));
  }, []);

  const extrasNameSet = useMemo(() => new Set(extras.map((e) => e.name)), [extras]);

  // case/accent-insensitive lookup: normalised(name) -> canonical CSV name
  const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim();
  const normToCanonical = useMemo(() => {
    const m = new Map();
    for (const e of extras) m.set(norm(e.name), e.name);
    return m;
  }, [extras]);

  // resolve a stored value to its canonical CSV form (or null if not in CSV)
  const canonicalOf = (v) => normToCanonical.get(norm(v)) || null;

  const filtered = useMemo(() => {
    if (!query.trim()) return extras;
    const q = query.toLowerCase();
    return extras.filter((e) => e.name.toLowerCase().includes(q));
  }, [extras, query]);

  // an item is "checked" if its canonical form is in value OR the raw value is in value
  const isChecked = (canonicalName) =>
    value.includes(canonicalName) || value.some((v) => norm(v) === norm(canonicalName));

  const toggle = (name) => {
    if (isChecked(name)) {
      // remove any variant (raw or canonical) that matches
      onChange(value.filter((v) => norm(v) !== norm(name)));
    } else {
      onChange([...value, name]);
    }
  };

  // an "orphan" is an existing value with NO car.gr match (case-insensitive)
  const orphans = value.filter((v) => !canonicalOf(v));
  const matchedNonCanonical = value.filter((v) => canonicalOf(v) && !extrasNameSet.has(v));
  const matchedCount = value.length - orphans.length;

  const normalizeAll = () => {
    // replace case-mismatched values with canonical CSV form; drop duplicates
    const seen = new Set();
    const out = [];
    for (const v of value) {
      const canon = canonicalOf(v);
      const key = canon || v;
      if (seen.has(norm(key))) continue;
      seen.add(norm(key));
      out.push(key);
    }
    onChange(out);
  };

  return (
    <div className="space-y-4" data-testid="equipment-selector">
      {/* Header stats + search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-black/10 pb-3">
        <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.18em] text-[#4B5563] flex-wrap">
          <span>
            Επιλεγμένα: <span className="font-mono text-[#0A0A0A]">{matchedCount}</span> / {extras.length}
          </span>
          {orphans.length > 0 && (
            <span className="text-[#DC2626]">
              Μη συμβατά: <span className="font-mono">{orphans.length}</span>
            </span>
          )}
          {matchedNonCanonical.length > 0 && (
            <button
              type="button"
              onClick={normalizeAll}
              className="text-[#0055FF] hover:underline normal-case tracking-normal"
              data-testid="equipment-normalize-btn"
              title="Διόρθωση case/orthography σε car.gr canonical names"
            >
              Διόρθωση {matchedNonCanonical.length} ονομάτων →
            </button>
          )}
        </div>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Αναζήτηση εξοπλισμού..."
          data-testid="equipment-search"
          className="px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm md:w-72"
        />
      </div>

      {/* car.gr-compliant extras grid */}
      <div className="border border-black/10 bg-white">
        <div className="px-4 py-2 bg-[#F9FAFB] border-b border-black/10 flex items-center justify-between">
          <h4 className="heading font-bold text-sm">car.gr Εξοπλισμός</h4>
          <span className="text-[10px] font-mono text-[#4B5563]">
            από extras.csv ({extras.length} items)
          </span>
        </div>
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-sm text-[#4B5563]">
            {extras.length === 0 ? "Φόρτωση..." : `Δεν βρέθηκε εξοπλισμός για "${query}"`}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-1.5 p-3">
            {filtered.map((it) => {
              const checked = isChecked(it.name);
              return (
                <label
                  key={it.id}
                  className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-[#F9FAFB] transition-colors border border-transparent ${
                    checked ? "border-[#0055FF] bg-[#0055FF]/5" : ""
                  }`}
                  data-testid={`equipment-${it.name}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(it.name)}
                    className="accent-[#0055FF] cursor-pointer"
                  />
                  <span className="text-sm truncate" title={it.name}>{it.name}</span>
                  <span className="ml-auto text-[9px] uppercase tracking-wider text-[#4B5563] font-mono">{it.id}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Orphan items from legacy data — not exportable to car.gr */}
      {orphans.length > 0 && (
        <div className="border border-[#FECACA] bg-[#FEF2F2]" data-testid="equipment-orphans">
          <div className="px-4 py-2 border-b border-[#FECACA] flex items-center justify-between">
            <h4 className="heading font-bold text-sm text-[#991B1B]">
              Μη συμβατά παλιά items (δεν εξάγονται στο car.gr)
            </h4>
            <span className="text-[10px] font-mono text-[#991B1B]">
              {orphans.length} items
            </span>
          </div>
          <div className="p-3 flex flex-wrap gap-1.5">
            {orphans.map((v) => (
              <span key={v} className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-[#FECACA] text-xs">
                {v}
                <button
                  type="button"
                  onClick={() => toggle(v)}
                  className="text-[#DC2626] hover:text-[#991B1B]"
                  title="Αφαίρεση"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Selected summary */}
      {value.length > 0 && (
        <div className="border border-black/10 bg-[#0A0A0A] text-white p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] mb-2">
            Επιλεγμένα ({value.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {value.map((v) => {
              const isMatched = !!canonicalOf(v);
              return (
                <span
                  key={v}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-mono ${
                    isMatched ? "bg-white/10" : "bg-[#DC2626]/30 text-white"
                  }`}
                  title={isMatched ? "car.gr-compliant" : "Δεν υπάρχει στο extras.csv"}
                >
                  {v}
                  <button onClick={() => toggle(v)} className="hover:text-[#E60000]" type="button">
                    <X size={12} />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
